import React, { useState } from 'react';
import Localize from 'react-intl-universal';
import { useNavigate } from 'react-router-dom';

import { intersection, get } from 'lodash-es';

import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import ArrowDropUpIcon from '@mui/icons-material/ArrowDropUp';
import GroupsIcon from '@mui/icons-material/Groups';
import HandshakeIcon from '@mui/icons-material/Handshake';
import LaunchIcon from '@mui/icons-material/Launch';
import LocationCityIcon from '@mui/icons-material/LocationCity';
import LogoutIcon from '@mui/icons-material/Logout';
import MicIcon from '@mui/icons-material/Mic';
import Avatar from '@mui/material/Avatar';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import { styled } from '@mui/material/styles';
import Typography from '@mui/material/Typography';

import img from '@assets/Images/avatar.jpg';
import DynamicComponent from '@components/DynamicComponent/DynamicComponent';
import { useGlobalContext } from '@hooks/Context/GlobalContext';
import * as routerConstants from '@navigation/Constants.js';
import tokenUtil from '@services/tokenUtil';

const StyledAvatar = styled(Avatar)(({ theme }) => ({
  height: '30px',
  width: '30px',
  backgroundColor: theme.palette.primary.light
}));

const StyledMenuItem = styled(MenuItem)(({ theme }) => ({
  color: theme.palette.primary.main,
  '& .MuiListItemIcon-root': {
    color: theme.palette.secondary.dark
  }
}));

const StyledMenu = styled(Menu)(() => ({
  marginTop: '45px',
  '& .MuiPaper-root.MuiPaper-elevation': {
    top: '-8px !important'
  }
}));

const StyledButton = styled(Button)(({ theme }) => ({
  backgroundColor: 'transparent',
  display: 'inline',
  color: theme.header.color,
  textTransform: 'none',
  fontSize: '1rem',
  minWidth: ' 55px',
  marginLeft: 0,
  '& .MuiButton-endIcon': {
    marginTop: '3px',
    marginLeft: '2px'
  }
}));

const HandleAvatar = ({ isLogin, header: { avatarIconName, avatarMenuItems }, user }) => {
  const navigate = useNavigate();
  const [contextData, setContextData] = useGlobalContext();
  const [anchorElUser, setAnchorElUser] = useState(null);

  const isImage = true; //TODO - we will use image from BE in the future, avatar.jpg will be removed
  const userRole = ['external trainers', 'internal trainers'];

  const handleOpenUserMenu = (event) => {
    if (!isLogin) {
      navigate(routerConstants.routes.path.login, {
        state: {
          from: '',
          path: ''
        }
      });
    } else {
      setAnchorElUser(event.currentTarget);
    }
  };

  const handleCloseUserMenu = (e) => {
    const route = e.target.id;
    const selected = itemsToMap.find((item) => item.route === route);
    const isExternal = get(selected, 'externalRoute');

    if (route === 'login') {
      setContextData({ ...contextData, loggedIn: false, user: null });
      tokenUtil.removeUser();
    }

    if (isExternal) {
      window.open(route, '_blank');
      return;
    }
    if (route) {
      navigate(`${routerConstants.routes.path[route]}`, {
        state: {
          from: '',
          path: ''
        }
      });
    }

    setAnchorElUser(null);
  };

  const itemsToMap = avatarMenuItems.items.filter(
    (item) =>
      (!item?.userRoles ||
        (item?.userRoles && intersection(item.userRoles, userRole).length > 0)) &&
      !item?.hidden
  );

  const componentRegistry = {
    AdminPanelSettingsIcon,
    GroupsIcon,
    MicIcon,
    LocationCityIcon,
    HandshakeIcon,
    AccountCircleIcon,
    LogoutIcon
  };

  return (
    <>
      {isImage && isLogin ? (
        <>
          <StyledButton
            variant='text'
            onClick={handleOpenUserMenu}
            sx={{ display: 'flex' }}
            startIcon={<StyledAvatar src={img} />}
            endIcon={anchorElUser ? <ArrowDropUpIcon /> : <ArrowDropDownIcon />}
          >
            <Typography>{user} </Typography>
          </StyledButton>

          <StyledMenu
            id='menu-appbar'
            anchorEl={anchorElUser}
            anchorOrigin={{
              vertical: 'top',
              horizontal: 'right'
            }}
            keepMounted
            transformOrigin={{
              vertical: 'top',
              horizontal: 'right'
            }}
            open={Boolean(anchorElUser)}
            onClose={handleCloseUserMenu}
          >
            {itemsToMap?.map((item) => (
              <StyledMenuItem
                key={item.name}
                id={item.route}
                disabled={item.disabled ? item.disabled : false}
                onClick={handleCloseUserMenu}
              >
                <ListItemIcon>
                  <DynamicComponent
                    sx={{
                      color: 'common.black'
                    }}
                    componentName={item.iconMui}
                    componentRegistry={componentRegistry}
                  />
                </ListItemIcon>
                {Localize.get(`AppNavBar.${item.name}`, item.name)}
                {item.externalRoute && <LaunchIcon sx={{ pl: 1 }} />}
              </StyledMenuItem>
            ))}
          </StyledMenu>
        </>
      ) : (
        <>
          <IconButton onClick={handleOpenUserMenu} sx={{ p: 0 }}>
            <StyledAvatar src={avatarIconName} />
          </IconButton>
          <StyledButton variant='text' onClick={handleOpenUserMenu}>
            {Localize.get('AppNavBar.logIn')}
          </StyledButton>
        </>
      )}
    </>
  );
};

export default HandleAvatar;
